import { createSvgElement } from '@ardoq/dom-utils';
import { Font } from '@ardoq/yfiles';
import {
  MODERNIZED_BLOCK_DIAGRAM_FONT_SIZE as FONT_SIZE,
  MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT as LINE_HEIGHT,
} from '@ardoq/graph-labeling';
import { colors } from '@ardoq/design-tokens';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import { LabelStyleProperties } from '@ardoq/export';
import { MODERNIZED_BLOCK_DIAGRAM_NODE_LABEL_FONT } from 'yfilesExtensions/styles/consts';

export const toHex = (num: number) => num.toString(16).padStart(2, '0');

export const rgbaToHex = (colorString: string): string => {
  const match = colorString.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
  );

  if (!match) {
    return '#000000';
  }

  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  const a = match[4] !== undefined ? parseFloat(match[4]) : 1;

  const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;

  if (a < 1) {
    const alphaHex = Math.round(a * 255)
      .toString(16)
      .padStart(2, '0');
    return `${hex}${alphaHex}`;
  }
  return hex;
};

const GROUP_SUBLABEL_FONT = new Font({
  fontFamily: ARDOQ_DEFAULT_FONT_FAMILY,
  fontSize: 14,
  fontWeight: 'normal',
});

export const getOpacity = (isTransparentized: boolean) =>
  isTransparentized ? 0.32 : 1;

export enum Preset {
  Other = 'other',
  GroupSub = 'group-sub',
  Modern = 'modern',
}

type LineInput = string | { text: string };
export type LabelContent =
  | { kind: 'text'; text: string }
  | { kind: 'lines'; lines: LineInput[] };

export type CommonOptions = Partial<{
  isTransparentized: boolean;
  textColor: string;
  fill: string;
  stroke: string;
  strokeWidth: number;
  fontSize: number;
  lineHeight: number;
  textAlign: 'left' | 'center' | 'right';
  dominantBaseline: string;
  verticalAlign: string;
  preset: Preset;
}>;

const fontProps = (font?: Font) =>
  font
    ? {
        fontSize: font.fontSize,
        fontFamily: font.fontFamily,
        fontStyle: font.fontStyle.toString(),
        fontWeight: font.fontWeight,
      }
    : {};

const layoutProps = (o: CommonOptions = {}) => ({
  fontSize: o.fontSize ?? FONT_SIZE,
  ...(o.lineHeight !== undefined ? { lineHeight: o.lineHeight } : {}),
  textAlign: o.textAlign ?? 'center',
  dominantBaseline: o.dominantBaseline ?? 'middle',
  ...(o.verticalAlign !== undefined ? { verticalAlign: o.verticalAlign } : {}),
});

const visualProps = (o: CommonOptions = {}) => ({
  textColor: o.textColor ?? colors.black950,
  ...(o.fill !== undefined ? { fill: o.fill } : {}),
  ...(o.stroke !== undefined ? { stroke: o.stroke } : {}),
  ...(o.strokeWidth !== undefined ? { strokeWidth: o.strokeWidth } : {}),
  ...(o.isTransparentized !== undefined
    ? { opacity: getOpacity(!!o.isTransparentized) }
    : {}),
});

const PRESET_VISUAL_DEFAULTS: Record<Preset, CommonOptions> = {
  [Preset.Other]: {
    stroke: colors.blue200,
    strokeWidth: 1,
    fill: colors.blue50,
    textColor: colors.black950,
  },
  [Preset.GroupSub]: {
    fill: rgbaToHex(colors.transparent0),
    textColor: colors.grey600,
  },
  [Preset.Modern]: {},
};

const PRESET_LAYOUT_DEFAULTS: Record<Preset, CommonOptions> = {
  [Preset.Other]: {
    fontSize: FONT_SIZE,
    lineHeight: LINE_HEIGHT,
    textAlign: 'center',
    dominantBaseline: 'middle',
  },
  [Preset.GroupSub]: { textAlign: 'left' },
  [Preset.Modern]: { verticalAlign: 'text-top' },
};

const PRESET_FONTS: Record<Preset, Font | null> = {
  [Preset.Other]: null,
  [Preset.GroupSub]: GROUP_SUBLABEL_FONT,
  [Preset.Modern]: MODERNIZED_BLOCK_DIAGRAM_NODE_LABEL_FONT,
};

export const createLabelStyle = (
  font: Font | null,
  content?: LabelContent,
  opts: CommonOptions = {}
): LabelStyleProperties => {
  const base: LabelStyleProperties = {};
  if (content?.kind === 'text') base.text = content.text;
  if (content?.kind === 'lines') {
    base.lines = content.lines.map(l =>
      typeof l === 'string' ? { text: l } : l
    );
  }

  const preset = opts.preset;
  const presetDefaults = preset
    ? { ...PRESET_VISUAL_DEFAULTS[preset], ...PRESET_LAYOUT_DEFAULTS[preset] }
    : {};
  const merged: CommonOptions = { ...presetDefaults, ...opts };

  const resolvedFont = font ?? (preset ? PRESET_FONTS[preset] : null);
  const mergedWithFontSize: CommonOptions = {
    ...merged,
    fontSize: merged.fontSize ?? resolvedFont?.fontSize,
  };

  return {
    ...base,
    ...fontProps(resolvedFont ?? undefined),
    ...layoutProps(mergedWithFontSize),
    ...visualProps(merged),
  };
};

export const createOtherLabelBackground = (
  x: number,
  y: number,
  width: number,
  height: number
) =>
  createSvgElement('rect', {
    x: `${x}`,
    y: `${y}`,
    width: `${width}`,
    height: `${height}`,
    rx: '4',
    ry: '4',
    stroke: colors.blue200,
    'stroke-width': '1',
    fill: colors.blue50,
  });
